import { RegularViewTableItemData } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import { WideViewOwnershipTableItemData } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Ownership/WideViewOwnershipTable/useWideViewOwnershipTableController';
import {
	ExtendedColumn,
	ExtendedColumnDef,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { Box, Menu, Text, Tooltip, rem } from '@mantine/core';
import { Column, Table } from '@tanstack/react-table';
import { useCallback, useEffect, useState } from 'react';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface Props<
	TableItemData extends RecordAny = WideViewOwnershipTableItemData | RegularViewTableItemData,
> {
	table: Table<TableItemData>;
}

export const HoldingCustomSettings = <
	TableItemData extends RecordAny = WideViewOwnershipTableItemData | RegularViewTableItemData,
>({
	table,
}: Props<TableItemData>): JSX.Element => {
	const tCapManage = useTranslationCapManage();

	const [customSecuritiesSettingsHeaders, setCustomSecuritiesSettingsHeaders] = useState<
		string[]
	>([]);
	const [customSecuritiesSettings, setCustomSecuritiesSettings] = useState<{
		[header: string]: boolean;
	}>({});

	const { options } = table;

	const [filteredColumns, setFilteredColumns] = useState<Column<TableItemData>[]>([]);

	useEffect(() => {
		const allColumns = table.getAllColumns();
		setFilteredColumns(
			allColumns.filter((column) => column.id !== 'actions' && column.id !== 'holder')
		);
	}, [table]);

	useEffect(() => {
		const settingsHeaders = [
			...new Set(
				filteredColumns.flatMap((column) =>
					column.columns
						.filter((c) => c.id !== 'holder-sub')
						.map((col) => col.columnDef.header)
				)
			),
		].filter((header): header is string => !!header);
		setCustomSecuritiesSettingsHeaders(settingsHeaders);
	}, [filteredColumns]);

	const getColumnsForSetting = useCallback(
		(columnHeader: string, columns: Column<TableItemData>[]) =>
			columns
				.flatMap((col) => col.columns || [])
				.filter((column) => column.columnDef.header === columnHeader),
		[]
	);

	useEffect(() => {
		const settings = customSecuritiesSettingsHeaders.reduce((acc, header: string) => {
			const columns = getColumnsForSetting(header, filteredColumns);
			return {
				...acc,
				[header]: !!columns.filter((column) => column.getIsVisible()).length,
			};
		}, {});
		setCustomSecuritiesSettings(settings);
	}, [customSecuritiesSettingsHeaders, filteredColumns, getColumnsForSetting]);

	const handleSecurityToggleColumnHidden = (checked: boolean, column: Column<TableItemData>) => {
		table.setColumnVisibility((prev) => ({
			...prev,
			[column.id]: checked,
		}));

		// Update child columns if they exist
		if (column.columns && column.columns.length > 0) {
			column.columns.forEach((childColumn: ExtendedColumn<TableItemData>) => {
				const header = childColumn.columnDef.header;
				const isCustomSettingChecked =
					typeof header === 'string' ? customSecuritiesSettings[header] : false;
				const childChecked = checked ? isCustomSettingChecked : false;

				table.setColumnVisibility((prev) => ({
					...prev,
					[childColumn.id]: childChecked,
				}));
			});
		}
	};

	const handleCustomToggleColumnHidden = (header: string) => {
		const checked = !customSecuritiesSettings[header];
		const columns = getColumnsForSetting(header, filteredColumns);

		table.setColumnVisibility((prev) => {
			const newState = { ...prev };
			columns.forEach((col) => {
				const isChecked = checked ? col?.parent?.getIsVisible() ?? false : false;
				newState[col.id] = isChecked;
			});
			return newState;
		});

		setCustomSecuritiesSettings({
			...customSecuritiesSettings,
			[header]: checked,
		});
	};

	return (
		<>
			{filteredColumns.map((column, index) => {
				const switchChecked = column.getIsVisible();
				const header = column.columnDef.header;
				const columnLabel = (
					column.columnDef as unknown as ExtendedColumnDef<TableItemData>
				).columnLabel;
				const columnHeader = typeof header === 'string' ? header : columnLabel ?? '';

				return (
					<SettingToggleRow
						key={index}
						columnHeader={columnHeader}
						enableHiding={options.enableHiding}
						switchChecked={switchChecked}
						columnDepth={column.depth}
						disabled={!column.getCanHide()}
						handleSwitchChange={() =>
							handleSecurityToggleColumnHidden(!switchChecked, column)
						}
						toggleVisibilityTooltipText={tCapManage('header_actions.toggle_visibility')}
					/>
				);
			})}
			<Menu.Divider />
			{customSecuritiesSettingsHeaders?.map((header, index) => {
				return (
					<SettingToggleRow
						key={index}
						columnHeader={header}
						enableHiding={options.enableHiding}
						switchChecked={customSecuritiesSettings[header]}
						handleSwitchChange={() => handleCustomToggleColumnHidden(header)}
						toggleVisibilityTooltipText={tCapManage('header_actions.toggle_visibility')}
					/>
				);
			})}
		</>
	);
};

const SettingToggleRow = ({
	columnHeader,
	enableHiding,
	switchChecked,
	columnDepth = 0,
	disabled,
	handleSwitchChange,
	toggleVisibilityTooltipText,
}: {
	columnHeader: string;
	enableHiding?: boolean;
	switchChecked?: boolean;
	disabled?: boolean;
	columnDepth?: number;
	handleSwitchChange?: () => void;
	toggleVisibilityTooltipText: string;
}) => {
	return (
		<Menu.Item
			component="span"
			sx={{
				alignItems: 'center',
				cursor: 'default',
				justifyContent: 'flex-start',
				paddingLeft: `${(columnDepth + 0.5) * 2}rem`,
				paddingTop: rem('6px'),
				paddingBottom: rem('6px'),
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexWrap: 'nowrap',
					gap: rem('8px'),
				}}
			>
				{enableHiding ? (
					<Tooltip withinPortal openDelay={1000} label={toggleVisibilityTooltipText}>
						<AdvancedSwitch
							selectedValue={switchChecked}
							rightText={columnHeader}
							onChange={() => handleSwitchChange && handleSwitchChange()}
							disabled={disabled}
							selectedColor="dark.8"
							unselectedColor="dark.8"
						/>
					</Tooltip>
				) : (
					<Text sx={{ alignSelf: 'center' }}>{columnHeader}</Text>
				)}
			</Box>
		</Menu.Item>
	);
};
